import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  scaleMeal,
  scaleMealIngredientsOperation,
} from '../../operations/meal_operations';
import {
  END_USER_RECIPE_COLLECTION_MEAL_DETAIL_ROUTE,
  END_USER_RECIPE_SINGLE_COLLECTION_ROUTE,
  END_USER_RECIPES_BOARD_ROUTE,
} from '../../services/routes';
import FullWidthMealCard from '../meal/full_width_meal_card';
import AddToPlannerModal from '../meal/add_to_planner_modal';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import EndUserOrganiseRecipeModal from '../meal/end_user_organise_recipe_modal';
import {
  mealFromSharedMeal,
  mealWithParentUpdated,
  mealWithScaledIngredientsReplaced,
  recipeCollectionsCardDOMID,
} from '../../services/meals';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useLazyGetSmorgBoardQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  boardWithMealAdded,
  boardWithMealMoved,
  boardWithMealRemoved,
} from '../../services/smorg_board';
import {
  mealsAddedToMealBasket,
  mealsRemovedFromMealBasket,
} from '../../features/end_user_shopping/end_user_shopping_slice';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { copyMealToPlanner } from '../../operations/planner_operations';
import { trackEvents, useTracker } from '../../operations/tracking_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import { isMealBackgroundProcessingSelector } from '../../features/recipes/recipes_slice';

const RecipeCollectionFullWidthMealCard = ({ mealID, parentID, menuID }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tracker = useTracker();

  const [addToPlannerAnchorEl, setAddToPlannerAnchorEl] = useState(null);

  const [copyToBoardAnchorEl, setCopyToBoardAnchorEl] = useState(null);

  const [deleteMealAnchorEl, setDeleteMealAnchorEl] = useState(null);

  const [scalingInProgress, setScalingInProgress] = useState(false);

  const [lazyGetSmorgBoard] = useLazyGetSmorgBoardQuery();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery({
    id: mealID,
    parentID,
  });

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery({ id: mealID, parentID });

  const distilledMeal = distilledMealInMeals || distilledMealInSharedMeals;

  const isBackgroundProcessing = useSelector((state) =>
    isMealBackgroundProcessingSelector(state, mealID),
  );

  const title = distilledMeal?.recipes[0]?.title;
  const servings = distilledMeal?.recipes[0]?.servings;

  const isSharedMeal = !!distilledMealInSharedMeals;

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();
  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [isAddingMealsToBasket, setIsAddingMealsToBasket] = useState(false);

  const anyCopyDestinationsAvailable =
    ownedRecipesBoards && ownedRecipesBoards.length > 0;

  const parentBoard = (ownedRecipesBoards || []).find((b) => b.id === parentID);

  const isInMealBasket = useSelector((state) =>
    state.endUserShopping.mealBasketItems.some(
      (item) => item.meal.id === mealID,
    ),
  );

  const calories =
    distilledMeal?.derivedNutrition?.totalNutritionPerServing?.calories;

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const onAddToMealBasket = useCallback(
    async (scaleToServings) => {
      setIsAddingMealsToBasket(true);
      try {
        trackEvents([
          {
            name: 'Meals added to basket',
            args: { source: 'recipeCollections' },
          },
        ]);
        let fullMeal;
        if (isSharedMeal) {
          const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
          fullMeal = mealFromSharedMeal(fullSharedMeal);
          fullMeal.id = mealID;
        } else {
          const { data } = await lazyGetMeal(mealID);
          fullMeal = data;
        }
        let scaledMeal;
        if (scaleToServings) {
          const scaleFactor =
            scaleToServings / (fullMeal.recipes[0]?.servings || 1);
          const scaledIngredients = await scaleMealIngredientsOperation(
            fullMeal,
            scaleFactor,
            customerFoodDataID,
          );
          scaledMeal = mealWithScaledIngredientsReplaced(
            fullMeal,
            scaleToServings,
            scaledIngredients,
            scaleFactor,
          );
        } else {
          scaledMeal = fullMeal;
        }
        dispatch(mealsAddedToMealBasket([scaledMeal]));
      } finally {
        setIsAddingMealsToBasket(false);
      }
    },
    [
      customerFoodDataID,
      dispatch,
      isSharedMeal,
      lazyGetMeal,
      lazyGetSharedMeal,
      mealID,
    ],
  );
  const onRemoveFromMealBasket = useCallback(
    () => dispatch(mealsRemovedFromMealBasket([mealID])),
    [dispatch, mealID],
  );
  const onCopyToPlanner = useCallback(
    async (plannerViewWeekStartDate, dayIndexes, scaleToServings) => {
      let fullMealToCopy;
      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
        fullMealToCopy = mealFromSharedMeal(fullSharedMeal);
      } else {
        const { data } = await lazyGetMeal(mealID);
        fullMealToCopy = data;
      }
      return copyMealToPlanner(
        fullMealToCopy,
        plannerViewWeekStartDate,
        dayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
        scaleToServings,
      );
    },
    [
      isSharedMeal,
      customerFoodDataID,
      tracker,
      userProfile,
      currentSpaceMembership,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      createMeal,
      updateMeal,
      updateDbPlannerWeek,
      lazyGetSharedMeal,
      mealID,
      lazyGetMeal,
    ],
  );

  const onCopyToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      const cb = (copiedMeal) => {
        window.setTimeout(() => {
          const el = document.getElementById(
            recipeCollectionsCardDOMID(copiedMeal.id),
          );
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }, 200);
      };

      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
        await copySharedMealToBoard(
          fullSharedMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          null,
          cb,
          dispatch,
        );
      } else {
        const { data: fullMeal } = await lazyGetMeal(mealID);
        await copyMealToBoard(
          fullMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          null,
          cb,
          dispatch,
          null,
        );
      }
    },
    [
      lazyGetSmorgBoard,
      isSharedMeal,
      lazyGetSharedMeal,
      mealID,
      currentSpaceMembership,
      updateSmorgBoard,
      createMeal,
      userProfile,
      tracker,
      dispatch,
      lazyGetMeal,
    ],
  );

  const onMoveToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      if (parentID === toRecipesBoardId) {
        const newBoard = boardWithMealMoved(
          parentBoard,
          mealID,
          menuID,
          toMenuId,
        );
        await updateSmorgBoard(newBoard);
        return;
      }
      const { data: fullMeal } = await lazyGetMeal(mealID);
      const updatedMeal = mealWithParentUpdated(fullMeal, toRecipesBoardId);
      await updateMeal(updatedMeal);
      const newSourceRecipesBoard = boardWithMealRemoved(
        parentBoard,
        mealID,
        menuID,
      );
      const newDestinationRecipesBoard = boardWithMealAdded(
        destinationRecipesBoard,
        toMenuId,
        { id: mealID },
      );
      await Promise.all(
        [newSourceRecipesBoard, newDestinationRecipesBoard].map((board) =>
          updateSmorgBoard(board),
        ),
      );
    },
    [
      lazyGetMeal,
      lazyGetSmorgBoard,
      mealID,
      menuID,
      parentBoard,
      parentID,
      updateMeal,
      updateSmorgBoard,
    ],
  );

  const onDelete = useCallback(async () => {
    dispatch(mealsRemovedFromMealBasket([mealID]));
    const updatedParentBoard = boardWithMealRemoved(
      parentBoard,
      mealID,
      menuID,
    );
    await Promise.all([
      deleteMeal({
        id: mealID,
        parentID,
      }),
      updateSmorgBoard(updatedParentBoard),
    ]);
  }, [
    dispatch,
    mealID,
    deleteMeal,
    parentID,
    parentBoard,
    menuID,
    updateSmorgBoard,
  ]);

  const onClickCopyToPlanner = useCallback((target) => {
    setAddToPlannerAnchorEl(target);
  }, []);

  const onClickCopyToRecipesBoard = useCallback((target) => {
    setCopyToBoardAnchorEl(target);
  }, []);

  const onClickDelete = useCallback((ev) => {
    setDeleteMealAnchorEl(ev.target);
    ev.stopPropagation();
  }, []);

  const onChangeScaleToServings = useCallback(
    async (scaleToServings) => {
      const scaleToServingsNumber = Number(scaleToServings) || 1;
      if (scaleToServingsNumber !== servings) {
        const scaleFactor = scaleToServingsNumber / (servings || 1);
        setScalingInProgress(true);
        try {
          // if (isSharedMeal) {
          //   await dispatch(
          //     locallyScaleSharedMealAction(
          //       mealID,
          //       scaleFactor,
          //       scaleToServingsNumber,
          //     ),
          //   );
          // } else {
          const { data: fullMeal } = await lazyGetMeal(mealID);
          await scaleMeal(
            fullMeal,
            scaleFactor,
            scaleToServingsNumber,
            customerFoodDataID,
            false,
            updateMeal,
            dispatch,
            lazyGetMeal,
          );
          // }
        } finally {
          setScalingInProgress(false);
        }
      }
    },
    [customerFoodDataID, dispatch, lazyGetMeal, mealID, servings, updateMeal],
  );

  const afterAddingToFavourites = useCallback(
    ({ favouritesRecipesBoardID }) => {
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Your meal has been added to your favourites',
          linkText: 'View',
          linkTarget: formatRoute(END_USER_RECIPE_SINGLE_COLLECTION_ROUTE, {
            boardID: favouritesRecipesBoardID,
          }),
        }),
      );
    },
    [dispatch],
  );

  if (!distilledMeal) {
    return null;
  }

  return (
    <>
      <FullWidthMealCard
        mealID={mealID}
        parentID={parentID}
        addToPlannerEnabled
        copyEnabled={!isSharedMeal && anyCopyDestinationsAvailable}
        shoppingEnabled
        deleteEnabled={!isSharedMeal}
        title={title}
        imageUrl={distilledMeal.recipes[0].imageUrl}
        mealTypes={distilledMeal.recipes[0].mealTypes}
        calories={calories}
        servingsEnabled
        servings={servings}
        onClick={() => {
          navigate(
            formatRoute(END_USER_RECIPE_COLLECTION_MEAL_DETAIL_ROUTE, {
              boardID: parentID,
              mealID,
            }),
          );
        }}
        onChangeScaleToServings={onChangeScaleToServings}
        isInMealBasket={isInMealBasket}
        isAddingMealsToBasket={isAddingMealsToBasket}
        onClickCopyToPlanner={onClickCopyToPlanner}
        onClickCopyToRecipesBoard={onClickCopyToRecipesBoard}
        onClickAddToMealBasket={onAddToMealBasket}
        onClickRemoveFromMealBasket={onRemoveFromMealBasket}
        onClickDelete={onClickDelete}
        scalingInProgress={scalingInProgress}
        favouriteEnabled={isSharedMeal}
        afterAddingToFavourites={afterAddingToFavourites}
        isBackgroundProcessing={isBackgroundProcessing}
      />
      {addToPlannerAnchorEl && (
        <AddToPlannerModal
          anchorEl={addToPlannerAnchorEl}
          copyToPlannerEnabled
          editServingsEnabled
          onCopyToPlanner={(
            plannerViewWeekStartDate,
            dayIndexes,
            scaleToServings,
          ) => {
            onCopyToPlanner(
              plannerViewWeekStartDate,
              dayIndexes,
              scaleToServings,
            );
            setAddToPlannerAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'Your meal has been copied to your planner',
                linkText: 'View planner',
                linkTarget: `/planner/${plannerViewWeekStartDate}`,
              }),
            );
          }}
          onDismiss={() => setAddToPlannerAnchorEl(null)}
          titleText="Schedule"
        />
      )}
      {copyToBoardAnchorEl && (
        <EndUserOrganiseRecipeModal
          recipesBoardId={parentID}
          menuSelectionEnabled
          anchorEl={copyToBoardAnchorEl}
          onCopy={(toRecipesBoardId, toMenuId) => {
            onCopyToRecipesBoard(toRecipesBoardId, toMenuId);
            setCopyToBoardAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'Your meal has been copied to your board',
                linkText: 'View board',
                linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
                  recipesBoardId: toRecipesBoardId,
                }),
              }),
            );
          }}
          isMoveEnabled={!isSharedMeal}
          onMove={(toRecipesBoardId, toMenuId) => {
            onMoveToRecipesBoard(toRecipesBoardId, toMenuId);
            setCopyToBoardAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'Your meal has been moved to your board',
                linkText: 'View board',
                linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
                  recipesBoardId: toRecipesBoardId,
                }),
              }),
            );
          }}
          onDismiss={() => setCopyToBoardAnchorEl(null)}
          boardTitle="collection"
        />
      )}
      <DeleteCardConfirmPopover
        visible={!!deleteMealAnchorEl}
        anchorEl={deleteMealAnchorEl}
        title="Delete meal"
        message="The meal will be deleted permanently. Are you sure?"
        height={140}
        onConfirmDelete={onDelete}
        onDismiss={() => setDeleteMealAnchorEl(null)}
      />
    </>
  );
};

RecipeCollectionFullWidthMealCard.propTypes = {
  mealID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
  menuID: PropTypes.string.isRequired,
};

export default RecipeCollectionFullWidthMealCard;
